/**
 * <s-product-carousel> — horizontal product row with previous/next buttons.
 *
 * Markup:
 *   [data-carousel-track] — the horizontally scrolling list (scroll-snap handles swipe on touch).
 *   autoplay [autoplay-interval=ms] — advance one screen at a time and wrap to the start;
 *   pauses on hover, focus, touch, off screen, hidden tab, and reduced-motion preference.
 *   Buttons inside or outside the element use commandfor="<carousel id>" with
 *   command="--carousel-prev" / "--carousel-next". They are disabled at the ends.
 */
class SProductCarousel extends HTMLElement {
  #ac;
  #frame = 0;
  #timer = 0;
  #hold = false;
  #onScreen = true;
  #io = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', this.#handleCommand, opts);
    this.#track()?.addEventListener('scroll', this.#queueUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#queueUpdate, { ...opts, passive: true });
    this.#update();
    if (this.hasAttribute('autoplay')) {
      const hold = () => { this.#hold = true; };
      const release = () => { this.#hold = false; };
      this.addEventListener('pointerenter', hold, opts);
      this.addEventListener('pointerleave', release, opts);
      this.addEventListener('focusin', hold, opts);
      this.addEventListener('focusout', release, opts);
      this.addEventListener('touchstart', hold, { ...opts, passive: true });
      this.addEventListener('touchend', () => setTimeout(release, 4000), { ...opts, passive: true });
      // Only auto-advance while the carousel is on screen: off-screen ticks still cost layout work.
      if ('IntersectionObserver' in window) {
        this.#io = new IntersectionObserver((entries) => {
          this.#onScreen = entries.some((entry) => entry.isIntersecting);
        });
        this.#io.observe(this);
      }
      const ms = Number(this.getAttribute('autoplay-interval')) || 5000;
      this.#timer = setInterval(() => this.#tick(), ms);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    clearInterval(this.#timer);
    this.#io?.disconnect();
  }

  #tick() {
    if (this.#hold || !this.#onScreen || document.hidden) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const track = this.#track();
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    if (max <= 2) return;
    if (Math.abs(track.scrollLeft) >= max - 2) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      const rtl = getComputedStyle(track).direction === 'rtl';
      const first = track.querySelector('li');
      const step = first ? first.getBoundingClientRect().width + 12 : track.clientWidth;
      track.scrollBy({ left: (rtl ? -1 : 1) * step, behavior: 'smooth' });
    }
  }

  #track() {
    return this.querySelector('[data-carousel-track]');
  }

  #buttons(command) {
    return [...document.querySelectorAll(`[commandfor="${this.id}"][command="${command}"]`)];
  }

  #handleCommand = (event) => {
    if (event.command !== '--carousel-prev' && event.command !== '--carousel-next') return;
    const track = this.#track();
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    let step = event.command === '--carousel-next' ? 1 : -1;
    if (rtl) step = -step;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: step * track.clientWidth * 0.9, behavior: smooth ? 'smooth' : 'auto' });
  };

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track();
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    const atStart = pos <= 2;
    const atEnd = pos >= max - 2;
    const rtl = getComputedStyle(track).direction === 'rtl';
    for (const btn of this.#buttons('--carousel-prev')) btn.disabled = rtl ? atEnd : atStart;
    for (const btn of this.#buttons('--carousel-next')) btn.disabled = rtl ? atStart : atEnd;
  }
}

if (!customElements.get('s-product-carousel')) {
  customElements.define('s-product-carousel', SProductCarousel);
}
